import type {
  BackendGenerationAction,
  GenerationAction,
} from "../context/types";

/**
 * Maps backend generation actions (flat target_panel + chip_id + free_text)
 * to frontend GenerationAction discriminated union.
 *
 * Reverses the transformation in toBackendActions (useGeneration.ts):
 * - floor + wall → interior_finishing
 * - style + room_type → furnishing
 * - wall_color → wall_color
 * - empty_room → remove_furniture
 * - free_wish → other_wishes
 */
export function mapBackendActionsToFrontend({
  actions,
}: {
  actions: BackendGenerationAction[] | null;
}): GenerationAction[] {
  if (!actions || actions.length === 0) return [];

  const result: GenerationAction[] = [];

  const floor = actions.find((a) => a.target_panel === "floor");
  const wall = actions.find((a) => a.target_panel === "wall");
  const style = actions.find((a) => a.target_panel === "style");
  const roomType = actions.find((a) => a.target_panel === "room_type");
  const wallColor = actions.find((a) => a.target_panel === "wall_color");
  const emptyRoom = actions.find((a) => a.target_panel === "empty_room");
  const freeWish = actions.find((a) => a.target_panel === "free_wish");

  if (floor || wall) {
    const extraWensen = floor?.free_text || wall?.free_text || "";
    const action: GenerationAction = {
      type: "interior_finishing",
      ...(floor?.chip_id ? { floor: floor.chip_id } : {}),
      ...(wall?.chip_id ? { wall: wall.chip_id } : {}),
      ...(extraWensen ? { extra_wensen: extraWensen } : {}),
    };
    result.push(action);
  }

  if (style || roomType) {
    const extraWensen = style?.free_text || "";
    const action: GenerationAction = {
      type: "furnishing",
      style: style?.chip_id ?? "",
      room_type: roomType?.chip_id ?? "",
      ...(extraWensen ? { extra_wensen: extraWensen } : {}),
    };
    result.push(action);
  }

  if (wallColor) {
    const action: GenerationAction = {
      type: "wall_color",
      ral_code: wallColor.chip_id ?? undefined,
      scope: "all",
      ...(wallColor.free_text
        ? wallColor.chip_id
          ? { extra_wensen: wallColor.free_text }
          : { custom_text: wallColor.free_text }
        : {}),
    };
    result.push(action);
  }

  const furnish = actions.find((a) => a.target_panel === "furnish");
  if (furnish && !style && !roomType) {
    result.push({
      type: "furnishing",
      style: "",
      room_type: "",
      ...(furnish.free_text ? { extra_wensen: furnish.free_text } : {}),
    });
  }

  if (emptyRoom) {
    result.push({ type: "remove_furniture" });
  }

  if (freeWish) {
    result.push({ type: "other_wishes", text: freeWish.free_text });
  }

  return result;
}
